import { Component, OnInit, OnDestroy } from '@angular/core';
import { DesignService } from '../../service/design.service';
import { PwaInstallService } from '../../service/pwa-install.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-install-app-button',
  templateUrl: './install-app-button.component.html',
  styleUrls: ['./install-app-button.component.scss']
})
export class InstallAppButton implements OnInit, OnDestroy {
  showBanner = false;
  installMessage = 'Download app';
  hasPWAConfig = false;
  isDismissed = false;
  isIOS = false;
  private deferredPrompt: any;
  private destroy$ = new Subject<void>();

  constructor(
    private designService: DesignService,
    private pwaInstallService: PwaInstallService
  ) {}

  ngOnInit() {
    this.detectIOS();
    this.checkPWAConfig();
    this.checkDismissed();
    
    this.deferredPrompt = this.pwaInstallService.getDeferredPrompt();
    
    this.pwaInstallService.deferredPrompt$
      .pipe(takeUntil(this.destroy$))
      .subscribe(prompt => {
        this.deferredPrompt = prompt;
        this.updateBannerVisibility();
      });
    
    this.setupBeforeInstallPrompt();
    this.checkIfInstalled();
    
    setTimeout(() => {
      this.deferredPrompt = this.pwaInstallService.getDeferredPrompt();
      this.updateBannerVisibility();
    }, 500);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private detectIOS() {
    const userAgent = window.navigator.userAgent.toLowerCase();
    this.isIOS = /iphone|ipad|ipod/.test(userAgent);
  }

  private checkPWAConfig() {
    const backofficeAttrs = this.designService.getSettings('backoffice');
    
    if (backofficeAttrs && backofficeAttrs.attributes) {
      const pwaAttr = backofficeAttrs.attributes.find(
        attr => attr.key && attr.key.toLowerCase() === 'pwa'
      );

      if (pwaAttr) {
        this.hasPWAConfig = true;
        
        if (pwaAttr.attributes) {
          const messageAttr = pwaAttr.attributes.find(
            attr => attr.key && attr.key.toLowerCase() === 'message'
          );
          
          if (messageAttr && messageAttr.value) {
            this.installMessage = messageAttr.value;
          }
        }
      } else {
        this.hasPWAConfig = false;
        return;
      }
    } else {
      const isLocalhost = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1';
      
      if (isLocalhost) {
        this.hasPWAConfig = true;
      } else {
        this.hasPWAConfig = false;
        return;
      }
    }
  }

  private setupBeforeInstallPrompt() {
    if ((window as any).deferredPrompt) {
      this.deferredPrompt = (window as any).deferredPrompt;
      this.updateBannerVisibility();
    }

    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.deferredPrompt = e;
      (window as any).deferredPrompt = e;
      this.updateBannerVisibility();
    });

    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      (window as any).deferredPrompt = null;
      this.dismissBanner(true);
    });
  }

  private checkIfInstalled() {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;
    
    if (isStandalone || isIOSStandalone) {
      this.showBanner = false;
      return;
    }

    this.updateBannerVisibility();
  }

  private updateBannerVisibility() {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;
    const isMobile = window.matchMedia('(max-width: 700px)').matches;
    
    const canInstall = this.isIOS || !!this.deferredPrompt;
    
    const shouldShow = this.hasPWAConfig &&
      canInstall &&
      !isStandalone &&
      !isIOSStandalone &&
      !this.isDismissed &&
      isMobile;
    
    this.showBanner = shouldShow;
  }

  async installApp() {
    if (this.isIOS) {
      return;
    }
    
    const installed = await this.pwaInstallService.promptInstall();
    
    if (installed) {
      this.deferredPrompt = null;
      this.dismissBanner(true);
    } else {
    }
    
    this.updateBannerVisibility();
  }

  dismissBanner(installed = false) {
    this.isDismissed = true;
    this.saveDismissed(installed);
    this.updateBannerVisibility();
  }

  private getDismissKey() {
    return `installAppBannerDismissed:${window.location.hostname}`;
  }

  private checkDismissed() {
    const value = localStorage.getItem(this.getDismissKey());
    this.isDismissed = value === 'true';
  }

  private saveDismissed(installed: boolean) {
    localStorage.setItem(this.getDismissKey(), 'true');
  }
}
